import {
  eachDayOfInterval,
  eachHourOfInterval,
  eachMinuteOfInterval,
  eachMonthOfInterval,
  isValid,
  parseISO,
  format,
  roundToNearestMinutes,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

export function createHistMap(start_time, end_time, binsize, data, timeField = "time_col") {
  const endTimeStamp =
    data && data?.msg && Array.isArray(data?.msg)
      ? data?.msg[data?.msg.length - 1][timeField]
      : "";

  // Create empty histogram
  const start = parseISO(
    format(start_time, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'")
  );
  let end;
  if (endTimeStamp) {
    end = parseISO(
      format(endTimeStamp, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'")
    );
  } else {
    end = parseISO(
      format(end_time, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'")
    );
  }
  // console.log("end", end);

  let timeSeries;
  if (binsize === "1h") {
    timeSeries = eachHourOfInterval({
      start: start,
      end: end,
    });
  } else if (binsize === "7d") {
    // Generate timeSeries for every 7 days
    const allDays = eachDayOfInterval({ start, end });
    timeSeries = allDays.filter(
      (date, idx) => idx % 7 === 0
    );
  } else if (binsize === "1d") {
    timeSeries = eachDayOfInterval({
      start: start,
      end: end,
    });
  } else if (binsize === "3m") {
    timeSeries = eachMinuteOfInterval({ start, end });
    // Generate timeSeries for every 3 minutes
    const allMinutes = eachMinuteOfInterval({ start, end });
    timeSeries = allMinutes.filter((date, idx) => {
      // Only keep minutes divisible by 3
      return date.getMinutes() % 3 === 0;
    });
  } else if (binsize === "minute") {
    timeSeries = eachMinuteOfInterval({ start, end });
  }
  const histMap = {};
  timeSeries?.forEach((time) => {
    // eslint-disable-next-line no-undef
    const dateString = format(time, "yyyy-MM-dd HH:mm:ss");
    histMap[dateString] = 0;
  });
  return histMap;
}

export function validateTimeStamps(data) {
  const start = parseISO(data.start);
  const end = parseISO(data.end);
  return isValid(start) && isValid(end);
}

export function validateBinsize(data) {
  if (!new Set(["1h", "1d", "1min"]).has(data.binsize)) {
    console.error('Invalid bin size: "%s"', data.binsize);
    throw new Error(('Invalid bin size: "%s"', data.binsize));
  }
}

export function convertTimestamp(timestamp, binsize) {
  let date;
  if (!isValid(timestamp)) return;

  // If timestamp is already a Date object, use it directly
  if (timestamp instanceof Date) {
    date = timestamp;
  } else {
    // If it's a string or other format, parse it
    date = parseISO(timestamp);
  }

  if (date !== undefined && isValid(date)) {
    if (binsize === "1d") {
      date.setHours(0, 0, 0, 0);
    }
    if (binsize === "7d") {
      date.setDate(1);
      date.setHours(0, 0, 0, 0);
    }
    if (binsize === "1h") {
      date.setMinutes(0, 0, 0);
    }
    if (binsize === "3m") {
      date.setSeconds(0, 0);
    }
  }

  return date !== undefined && isValid(date)
    ? format(date, "yyyy-MM-dd HH:mm:ss")
    : "";
}

export const generateDataForGraphs = (
  startTime,
  endTime,
  binsize,
  data,
  ledData = null
) => {
  // console.log("generateDataForGraphs called with:", {
  //   startTime,
  //   endTime,
  //   binsize,
  //   data,
  //   ledData,
  // });

  // Validate input parameters
  if (!startTime || !endTime || !binsize) {
    console.warn("generateDataForGraphs: Missing required parameters");
    return [];
  }

  // Ensure startTime and endTime are valid
  let validStartTime, validEndTime;
  try {
    if (startTime instanceof Date) {
      validStartTime = startTime;
    } else if (typeof startTime === "string") {
      validStartTime = parseISO(startTime);
    } else {
      validStartTime = new Date(startTime);
    }

    if (endTime instanceof Date) {
      validEndTime = endTime;
    } else if (typeof endTime === "string") {
      validEndTime = parseISO(endTime);
    } else {
      validEndTime = new Date(endTime);
    }

    // Validate that we have valid dates
    if (!isValid(validStartTime) || !isValid(validEndTime)) {
      console.warn("generateDataForGraphs: Invalid date values after parsing");
      return [];
    }
  } catch (error) {
    console.error("generateDataForGraphs: Error parsing dates:", error);
    return [];
  }

  let keysWithHistMap = [];
  if (
    data && data.msg &&
    Array.isArray(data.msg) &&
    data.msg.length > 0
  ) {
    const KeysToBeMapped = Object.keys(data.msg[0]).filter(
      (key) => key !== "time_col"
    );
    keysWithHistMap = KeysToBeMapped.map((key) => {
      return {
        [key]: createHistMap(startTime, endTime, binsize, data),
      };
    });

    // we extract each item and set the data from the response

    data.msg.forEach((item) => {
      // we iterate over each item pick the keys. Iterate over the keys and look for that key in the list of keysWithHistMap
      // if the key is found we pick the time_col and perform date conversion. We try to find the same date in the histmap
      //if date is found we update the value of the key in the histmap
      Object.keys(item).forEach((key) => {
        const isoTimeStamp = item?.time_col
          ?.replace(" ", "T") // Replace the space with "T"
          .replace(/(\.\d{3})\d+$/, "$1") // Keep only the first 3 digits after the decimal point
          .concat("Z");
        const parsedDate = parseISO(isoTimeStamp);
        const time = convertTimestamp(parsedDate, binsize);
        keysWithHistMap.forEach((histMap) => {
          const key = Object.keys(histMap)[0];
          if (key in item) {
            if (histMap[key][time] !== undefined) {
              histMap[key][time] = item[key];
            }
          }
        });
      });
    });
  }

  // Process LED data for issues if available
  if (ledData && Array.isArray(ledData.msg) && ledData.msg.length > 0) {
    // Check if LED data has an error status
    if (ledData.status && ledData.status.toLowerCase() === "error") {
      console.warn("LED data has error status:", ledData.msg);
      return keysWithHistMap;
    }

    // Validate LED data structure
    const validLedItems = ledData.msg.filter(item =>
      item && typeof item === 'object' &&
      item.time && typeof item.time === 'string' &&
      item.issue && typeof item.issue === 'string' &&
      item.count !== undefined && !isNaN(Number(item.count))
    );

    if (validLedItems.length === 0) {
      console.warn("No valid LED data items found. Expected structure: { time: string, issue: string, count: number }");
      return keysWithHistMap;
    }

    console.log("Processing LED data:", validLedItems);
    console.log("Time range for histograms:", { start: validStartTime, end: validEndTime, binsize });

    // Use the same time range as the main dashboard data for consistency
    // Create histogram for issues data using the SAME time range as main data
    const issuesHistMap = createHistMap(validStartTime, validEndTime, binsize, data);

    // Initialize separate histograms for each issue type using the SAME time range
    const hasboardIssuesHistMap = createHistMap(validStartTime, validEndTime, binsize, data);
    const psuIssuesHistMap = createHistMap(validStartTime, validEndTime, binsize, data);
    const fanIssuesHistMap = createHistMap(validStartTime, validEndTime, binsize, data);
    const configInvalidHistMap = createHistMap(validStartTime, validEndTime, binsize, data);
    const leakDetectedHistMap = createHistMap(validStartTime, validEndTime, binsize, data);

    console.log("Created histograms with keys:", {
      hasboard: Object.keys(hasboardIssuesHistMap).slice(0, 5), // Show first 5 keys
      psu: Object.keys(psuIssuesHistMap).slice(0, 5),
      fan: Object.keys(fanIssuesHistMap).slice(0, 5),
      config: Object.keys(configInvalidHistMap).slice(0, 5),
      leak: Object.keys(leakDetectedHistMap).slice(0, 5)
    });

    // Process each LED data entry
    validLedItems.forEach((item) => {
      console.log("Processing LED item:", item);

      // Convert time format from LED API to match the histogram format
      let isoTimeStamp;
      let parsedDate;
      let time;

      try {
        // Try different time format parsing strategies
        let timeStr = item.time;

        // First try: "2025-07-31 00:00:00.000000000" -> "2025-07-31T00:00:00.000Z"
        if (timeStr.includes(" ") && timeStr.includes(".")) {
          isoTimeStamp = timeStr
            .replace(" ", "T") // Replace the space with "T"
            .replace(/(\.\d{3})\d+$/, "$1") // Keep only the first 3 digits after the decimal point
            .concat("Z");
        }
        // Second try: if it's already in ISO format
        else if (timeStr.includes("T")) {
          isoTimeStamp = timeStr;
        }
        // Third try: if it's just a date string
        else {
          isoTimeStamp = timeStr + "T00:00:00.000Z";
        }

        parsedDate = parseISO(isoTimeStamp);
        time = convertTimestamp(parsedDate, binsize);

        console.log("Converted time:", {
          original: item.time,
          isoTimeStamp,
          parsed: parsedDate,
          converted: time,
          isValid: isValid(parsedDate)
        });
      } catch (error) {
        console.error("Error parsing time:", error, "for item:", item);
        return;
      }

      // Skip if time conversion failed or time not found in histogram
      if (!time || !isValid(parsedDate)) {
        console.warn("Time conversion failed for item:", item);
        return;
      }

      // Map issue types to their respective histograms
      switch (item.issue) {
        case "Hashboard Malfunctions":
        case "Hashboard malfunction":
        case "Hash board malfunction":
          if (hasboardIssuesHistMap[time] !== undefined) {
            hasboardIssuesHistMap[time] += item.count;
            console.log("Added to hasboard issues:", { time, count: item.count, total: hasboardIssuesHistMap[time] });
          } else {
            console.warn("Time not found in hasboard histogram:", time);
          }
          break;
        case "PSU Malfunction":
        case "PSU malfunction":
          if (psuIssuesHistMap[time] !== undefined) {
            psuIssuesHistMap[time] += item.count;
            console.log("Added to PSU issues:", { time, count: item.count, total: psuIssuesHistMap[time] });
          } else {
            console.warn("Time not found in PSU histogram:", time);
          }
          break;
        case "Fan Malfunction":
        case "Fan malfunction":
          if (fanIssuesHistMap[time] !== undefined) {
            fanIssuesHistMap[time] += item.count;
            console.log("Added to fan issues:", { time, count: item.count, total: fanIssuesHistMap[time] });
          } else {
            console.warn("Time not found in fan histogram:", time);
          }
          break;
        case "Invalid Pool Config":
          if (configInvalidHistMap[time] !== undefined) {
            configInvalidHistMap[time] += item.count;
            console.log("Added to config issues:", { time, count: item.count, total: configInvalidHistMap[time] });
          } else {
            console.warn("Time not found in config histogram:", time);
          }
          break;
        case "Leak Detected":
          if (leakDetectedHistMap[time] !== undefined) {
            leakDetectedHistMap[time] += item.count;
            console.log("Added to leak issues:", { time, count: item.count, total: leakDetectedHistMap[time] });
          } else {
            console.warn("Time not found in leak histogram:", time);
          }
          break;
        default:
          // Log any unmatched issue types for debugging
          console.log("Unmatched issue type:", item.issue);
          break;
      }
    });

    console.log("Final histograms:", {
      hasboard: hasboardIssuesHistMap,
      psu: psuIssuesHistMap,
      fan: fanIssuesHistMap,
      config: configInvalidHistMap,
      leak: leakDetectedHistMap
    });

    // Log summary of processed issues
    const totalProcessed = validLedItems.length;
    const totalHashboard = Object.values(hasboardIssuesHistMap).reduce((sum, val) => sum + val, 0);
    const totalPSU = Object.values(psuIssuesHistMap).reduce((sum, val) => sum + val, 0);
    const totalFan = Object.values(fanIssuesHistMap).reduce((sum, val) => sum + val, 0);
    const totalConfig = Object.values(configInvalidHistMap).reduce((sum, val) => sum + val, 0);
    const totalLeak = Object.values(leakDetectedHistMap).reduce((sum, val) => sum + val, 0);

    console.log("Issues processing summary:", {
      totalProcessed,
      totalHashboard,
      totalPSU,
      totalFan,
      totalConfig,
      totalLeak
    });

    // Add all issue types to the return data
    keysWithHistMap.push({
      hasboard_issues: hasboardIssuesHistMap,
      psu_issues: psuIssuesHistMap,
      fan_issues: fanIssuesHistMap,
      config_invalid: configInvalidHistMap,
      leak_detected: leakDetectedHistMap,
    });

    // console.log("Successfully processed LED data and added issues to graph data");
  } else {
    // console.log("No LED data available for issues graph");

    // Create empty histograms to prevent the graph from breaking
    const emptyHistMap = createHistMap(validStartTime, validEndTime, binsize, data);

    keysWithHistMap.push({
      hasboard_issues: emptyHistMap,
      psu_issues: emptyHistMap,
      fan_issues: emptyHistMap,
      config_invalid: emptyHistMap,
      leak_detected: emptyHistMap,
    });
  }

  return keysWithHistMap;
};

const createGraphData = (lowData, mediumData, highData) => {
  if (!lowData || !mediumData || !highData) return;

  // Undo trailing zero trimming; use full arrays
  const trimmedLow = [...lowData];
  const trimmedMedium = [...mediumData];
  const trimmedHigh = [...highData];

  const data = [
    {
      name: "Minimum",
      data: trimmedLow,
      sum: 173,
      color: "#FFD7F6",
      type: "spline",
      visible: true,
    },
    {
      name: "Average",
      data: trimmedMedium,
      sum: 40,
      color: "#B6C4FF",
      type: "spline",
      visible: true,
    },
    {
      name: "Maximum",
      data: trimmedHigh,
      sum: 39,
      color: "#2D55C9",
      type: "spline",
      visible: true,
    },
  ];
  return data;
};

const provideGraphConfig = (data, xAxisValues) => {
  if (!data || !xAxisValues) return;
  return {
    chart: {
      type: "spline",
      height: 180,
      spacingLeft: 24,
      spacingRight: 24,
    },
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      spline: {
        lineWidth: 2,
        lineCap: "round",
        softThreshold: true,
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: true,
            },
          },
        },
      },
    },

    xAxis: {
      lineColor: "#e0e0e0",
      categories: [...xAxisValues],
      tickWidth: 2,
      tickmarkPlacement: "on",
      align: "center", // Align the labels to the center of the tick
      tickColor: "#e0e0e0",
      labels: {
        style: {
          fontFamily: "roboto",
          fontWeight: 400,
          fontSize: "0.8rem",
          color: "#44474E",
        },
      },
    },
    yAxis: {
      startOnTick: true,
      endOnTick: true,
      opposite: false,

      gridLineColor: "#E0E0E0",
      title: {
        text: "",
      },
      min: 0,
      labels: {
        format: "{value:.0f}",
        style: {
          fontFamily: "roboto",
          fontWeight: 400,
          fontSize: "0.8rem",
          color: "#44474E",
        },
      },
    },
    series: [...data],
    legend: {
      enabled: true,
      align: "left",
    },
  };
};

export const generateConfigForGraphs = (data, binSize) => {
  if (!data) return;

  const calculateAverages = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return;
    }

    const numArrays = data.length;
    const numElements = data[0].length;

    const sums = data.reduce((acc, array) => {
      return acc.map((sum, index) => sum + array[index]);
    }, new Array(numElements).fill(0));

    return sums.map((sum) => parseFloat((sum / numArrays).toFixed(2)));
  };

  const hashRateData = data.filter((hashrate) =>
    Object.keys(hashrate)[0].includes("hash")
  );
  const hashRateMin = hashRateData.filter((hashrate) =>
    Object.keys(hashrate)[0].includes("min")
  );
  const hashRateMax = hashRateData.filter((hashrate) =>
    Object.keys(hashrate)[0].includes("max")
  );
  const hashRateAvg = hashRateData.filter((hashrate) =>
    Object.keys(hashrate)[0].includes("avg")
  );

  const xAxisValues =
    hashRateMin[0] && Object.keys(Object.values(hashRateMin[0])[0]);

  const modifiedXAxisValues =
    Array.isArray(xAxisValues) &&
    xAxisValues.map((time) => {
      let utcTimeString = time;

      return binSize === "3m"
        ? format(parseISO(utcTimeString), "hh:mm a")
        : binSize === "1h"
        ? format(parseISO(utcTimeString), "MM/dd/yy hh:mm a")
        : binSize === "1d"
        ? format(parseISO(utcTimeString), "MM/dd/yy")
        : format(parseISO(utcTimeString), "MM/dd/yy");
    });

  const hashRateLowData =
    hashRateMin[0] && Object.values(Object.values(hashRateMin[0])[0]);
  const hashRateAvgData =
    hashRateAvg[0] && Object.values(Object.values(hashRateAvg[0])[0]);
  const hashRateMaxData =
    hashRateMax[0] && Object.values(Object.values(hashRateMax[0])[0]);

  const hashRateGraphData = createGraphData(
    hashRateLowData,
    hashRateAvgData,
    hashRateMaxData
  );
  const hashRateGraphConfig = provideGraphConfig(
    hashRateGraphData,
    modifiedXAxisValues
  );

  //extract power related info and create graph data
  const powerData = data.filter((power) =>
    Object.keys(power)[0].includes("power")
  );
  const powerMin = powerData.filter((power) =>
    Object.keys(power)[0].includes("min")
  );
  const powerMax = powerData.filter((power) =>
    Object.keys(power)[0].includes("max")
  );
  const powerAvg = powerData.filter((power) =>
    Object.keys(power)[0].includes("avg")
  );
  const powerLowData =
    powerMin[0] && Object.values(Object.values(powerMin[0])[0]);
  const powerAvgData =
    powerAvg[0] && Object.values(Object.values(powerAvg[0])[0]);
  const powerMaxData =
    powerMax[0] && Object.values(Object.values(powerMax[0])[0]);

  const powerGraphData = createGraphData(
    powerLowData,
    powerAvgData,
    powerMaxData
  );
  const powerGraphConfig = provideGraphConfig(
    powerGraphData,
    modifiedXAxisValues
  );

  //extract temperature related info and create graph data
  const temperatureData = data.filter((temperature) =>
    Object.keys(temperature)[0].includes("temp") && !Object.keys(temperature)[0].includes("temp_cb")
  );
  const temperatureMin = temperatureData.filter((temperature) =>
    Object.keys(temperature)[0].includes("min")
  );
  const temperatureMax = temperatureData.filter((temperature) =>
    Object.keys(temperature)[0].includes("max")
  );
  const temperatureAvg = temperatureData.filter((temperature) =>
    Object.keys(temperature)[0].includes("avg")
  );

  const tempuratureLowData =
    Array.isArray(temperatureMin) && temperatureMin.length > 0
      ? temperatureMin.map((tempuratureData) => {
          const [key, tempuratureValues] = Object.entries(tempuratureData)[0];
          const tempurature = tempuratureValues
            ? Object.values(tempuratureValues)
            : [];
          return tempurature.map((temp) => Number(temp));
        })
      : [];
  const tempuratureLowAvgData = calculateAverages(tempuratureLowData);

  const tempuratureAvgData =
    Array.isArray(temperatureAvg) && temperatureAvg.length > 0
      ? temperatureAvg.map((tempuratureData) => {
          const [key, tempuratureValues] = Object.entries(tempuratureData)[0];
          const tempurature = tempuratureValues
            ? Object.values(tempuratureValues)
            : [];
          return tempurature.map((temp) => Number(temp));
        })
      : [];
  const tempuratureAvgAvgData = calculateAverages(tempuratureAvgData);

  const tempuratureMaxData =
    Array.isArray(temperatureMax) && temperatureMax.length > 0
      ? temperatureMax.map((tempuratureData) => {
          const [key, tempuratureValues] = Object.entries(tempuratureData)[0];
          const tempurature = tempuratureValues
            ? Object.values(tempuratureValues)
            : [];
          return tempurature.map((temp) => Number(temp));
        })
      : [];
  const tempuratureMaxAvgData = calculateAverages(tempuratureMaxData);

  const tempuratureGraphData = createGraphData(
    tempuratureLowAvgData,
    tempuratureAvgAvgData,
    tempuratureMaxAvgData
  );
  const tempuratureGraphConfig = provideGraphConfig(
    tempuratureGraphData,
    modifiedXAxisValues
  );

  //extract control board temperature related info and create graph data
  const controlBoardTempData = data.filter((temperature) =>
    Object.keys(temperature)[0].includes("temp_cb")
  );
  const controlBoardTempMin = controlBoardTempData.filter((temperature) =>
    Object.keys(temperature)[0].includes("min")
  );
  const controlBoardTempMax = controlBoardTempData.filter((temperature) =>
    Object.keys(temperature)[0].includes("max")
  );
  const controlBoardTempAvg = controlBoardTempData.filter((temperature) =>
    Object.keys(temperature)[0].includes("avg")
  );

  const controlBoardTempLowData =
    Array.isArray(controlBoardTempMin) && controlBoardTempMin.length > 0
      ? controlBoardTempMin.map((tempuratureData) => {
          const [key, tempuratureValues] = Object.entries(tempuratureData)[0];
          const tempurature = tempuratureValues
            ? Object.values(tempuratureValues)
            : [];
          return tempurature.map((temp) => Number(temp));
        })
      : [];
  const controlBoardTempLowAvgData = calculateAverages(controlBoardTempLowData);

  const controlBoardTempAvgData =
    Array.isArray(controlBoardTempAvg) && controlBoardTempAvg.length > 0
      ? controlBoardTempAvg.map((tempuratureData) => {
          const [key, tempuratureValues] = Object.entries(tempuratureData)[0];
          const tempurature = tempuratureValues
            ? Object.values(tempuratureValues)
            : [];
          return tempurature.map((temp) => Number(temp));
        })
      : [];
  const controlBoardTempAvgAvgData = calculateAverages(controlBoardTempAvgData);

  const controlBoardTempMaxData =
    Array.isArray(controlBoardTempMax) && controlBoardTempMax.length > 0
      ? controlBoardTempMax.map((tempuratureData) => {
          const [key, tempuratureValues] = Object.entries(tempuratureData)[0];
          const tempurature = tempuratureValues
            ? Object.values(tempuratureValues)
            : [];
          return tempurature.map((temp) => Number(temp));
        })
      : [];
  const controlBoardTempMaxAvgData = calculateAverages(controlBoardTempMaxData);

  const controlBoardTempGraphData = createGraphData(
    controlBoardTempLowAvgData,
    controlBoardTempAvgAvgData,
    controlBoardTempMaxAvgData
  );
  const controlBoardTempGraphConfig = provideGraphConfig(
    controlBoardTempGraphData,
    modifiedXAxisValues
  );

  //extract uptime related info and create graph data
  const uptimeData = data.filter((uptime) =>
    Object.keys(uptime)[0].includes("uptime")
  );
  const uptimeMin = uptimeData.filter((uptime) =>
    Object.keys(uptime)[0].includes("min")
  );
  const uptimeMax = uptimeData.filter((uptime) =>
    Object.keys(uptime)[0].includes("max")
  );
  const uptimeAvg = uptimeData.filter((uptime) =>
    Object.keys(uptime)[0].includes("avg")
  );
  const uptimeLowData =
    uptimeMin[0] && Object.values(Object.values(uptimeMin[0])[0]);
  const uptimeAvgData =
    uptimeAvg[0] && Object.values(Object.values(uptimeAvg[0])[0]);
  const uptimeMaxData =
    uptimeMax[0] && Object.values(Object.values(uptimeMax[0])[0]);

  const uptimeLowDataInMins =
    uptimeLowData &&
    uptimeLowData.map((uptime) => roundToNearestMinutes(uptime)?.getMinutes());
  const uptimeAvgDataInMins =
    uptimeLowData &&
    uptimeAvgData.map((uptime) => roundToNearestMinutes(uptime)?.getMinutes());
  const uptimeMaxDataInMins =
    uptimeLowData &&
    uptimeMaxData.map((uptime) => roundToNearestMinutes(uptime)?.getMinutes());

  const uptimeGraphData = createGraphData(
    uptimeLowDataInMins,
    uptimeAvgDataInMins,
    uptimeMaxDataInMins
  );
  const uptimeGraphConfig = provideGraphConfig(
    uptimeGraphData,
    modifiedXAxisValues
  );

  // Extract issues related info and create graph data
  // Look for the object that contains all issue types
  const allIssuesData = data.find(issues =>
    issues && typeof issues === 'object' &&
    (issues.hashboard_issues || issues.psu_issues || issues.fan_issues || issues.config_invalid || issues.leak_detected)
  );

  let issuesGraphData = [];
  let hasIssuesData = false;

  if (allIssuesData) {
    // Extract data for each issue type directly from the object
    const hasboardData = allIssuesData.hashboard_issues ? Object.values(allIssuesData.hashboard_issues) : [];
    const psuData = allIssuesData.psu_issues ? Object.values(allIssuesData.psu_issues) : [];
    const fanData = allIssuesData.fan_issues ? Object.values(allIssuesData.fan_issues) : [];
    const configData = allIssuesData.config_invalid ? Object.values(allIssuesData.config_invalid) : [];
    const leakData = allIssuesData.leak_detected ? Object.values(allIssuesData.leak_detected) : [];

    // Check if we have any actual data (not just zeros)
    hasIssuesData = [hasboardData, psuData, fanData, configData, leakData].some(arr =>
      arr && arr.length > 0 && arr.some(val => val > 0)
    );

    if (hasIssuesData) {
      issuesGraphData = [
        {
          name: "Leak Detected",
          data: leakData,
          sum: leakData.reduce((a, b) => a + b, 0),
          color: "#C62828",
          type: "spline",
          visible: true,
        },
        {
          name: "PSU Malfunction",
          data: psuData,
          sum: psuData.reduce((a, b) => a + b, 0),
          color: "#FF7F00",
          type: "spline",
          visible: true,
        },
        {
          name: "Hashboard Malfunction",
          data: hasboardData,
          sum: hasboardData.reduce((a, b) => a + b, 0),
          color: "#F9A825",
          type: "spline",
          visible: true,
        },
        {
          name: "Fan Malfunction",
          data: fanData,
          sum: fanData.reduce((a, b) => a + b, 0),
          color: "#039BE5",
          type: "spline",
          visible: true,
        },
        {
          name: "Invalid Pool Config",
          data: configData,
          sum: configData.reduce((a, b) => a + b, 0),
          color: "#8E24AA",
          type: "spline",
          visible: true,
        },
      ];
    }
  }

  const issuesGraphConfig = provideGraphConfig(
    issuesGraphData,
    modifiedXAxisValues
  );

  return {
    hashRateGraphConfig,
    powerGraphConfig,
    tempuratureGraphConfig,
    controlBoardTempGraphConfig,
    uptimeGraphConfig,
    issuesGraphConfig,
    hasIssuesData,
  };
};
